import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.in', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com',
  'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'protonmail.com', 'proton.me', 'zoho.com', 'gmx.com',
  'mail.com', 'yandex.com', 'yandex.ru', 'rediffmail.com',
  'qq.com', '163.com', '126.com', 'naver.com',
]);
const isBusinessEmail = (email) => {
  if (!email || !email.includes('@')) return false;
  const dom = email.split('@').pop().trim().toLowerCase();
  if (!dom.includes('.')) return false;
  return !FREE_DOMAINS.has(dom);
};

const SignInDialog = ({ open, onOpenChange, initialMode = 'signin' }) => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [mode, setMode] = useState(initialMode);

  const [signin, setSignin] = useState({ email: '', password: '' });
  const [signup, setSignup] = useState({ name: '', email: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState('idle'); // idle | loading | sent | error
  const [forgotMessage, setForgotMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const [serverMessage, setServerMessage] = useState('');

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setSignin({ email: '', password: '' });
        setSignup({ name: '', email: '', password: '' });
        setForgotEmail('');
        setForgotStatus('idle');
        setForgotMessage('');
        setErrors({});
        setShowPwd(false);
        setStatus('idle');
        setServerMessage('');
        setMode(initialMode);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open, initialMode]);

  const setField = (target, key) => (e) => {
    if (target === 'signin') setSignin((s) => ({ ...s, [key]: e.target.value }));
    else setSignup((s) => ({ ...s, [key]: e.target.value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validateSignin = () => {
    const er = {};
    if (!signin.email.trim()) er.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signin.email)) er.email = 'Enter a valid email';
    if (!signin.password) er.password = 'Please enter your password';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const validateSignup = () => {
    const er = {};
    if (!signup.name.trim()) er.name = 'Please enter your full name';
    const em = signup.email.trim();
    if (!em) er.email = 'Please enter your company email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) er.email = 'Enter a valid email';
    else if (!isBusinessEmail(em)) er.email = 'Please use your company email (free providers are not accepted)';
    if (!signup.password) er.password = 'Please choose a password';
    else if (signup.password.length < 8) er.password = 'Password must be at least 8 characters';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSignin = async (e) => {
    e.preventDefault();
    setServerMessage('');
    if (!validateSignin()) return;
    setStatus('loading');
    try {
      const res = await axios.post(
        `${API}/auth/login`,
        { email: signin.email.trim().toLowerCase(), password: signin.password },
        { withCredentials: true },
      );
      await refresh();
      onOpenChange(false);
      const role = res.data?.user?.role;
      if (role === 'employee') navigate('/employee-portal');
      else if (role === 'partner') navigate('/partner-portal');
      else if (role === 'superadmin') navigate('/admin');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Sign-in failed. Please try again.';
      setServerMessage(typeof detail === 'string' ? detail : 'Sign-in failed. Please try again.');
      setStatus('error');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setServerMessage('');
    if (!validateSignup()) return;
    setStatus('loading');
    try {
      const res = await axios.post(
        `${API}/auth/register`,
        {
          name: signup.name.trim(),
          email: signup.email.trim().toLowerCase(),
          password: signup.password,
        },
        { withCredentials: true },
      );
      await refresh();
      onOpenChange(false);
      const role = res.data?.user?.role;
      if (role === 'employee') navigate('/employee-portal');
      else if (role === 'partner') navigate('/partner-portal');
      else if (role === 'superadmin') navigate('/admin');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Sign-up failed. Please try again.';
      setServerMessage(typeof detail === 'string' ? detail : 'Sign-up failed. Please try again.');
      setStatus('error');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotMessage('');
    const em = forgotEmail.trim();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setForgotStatus('error');
      setForgotMessage('Please enter a valid email address.');
      return;
    }
    setForgotStatus('loading');
    try {
      const res = await axios.post(
        `${API}/auth/forgot-password`,
        { email: em.toLowerCase() },
      );
      setForgotStatus('sent');
      setForgotMessage(res.data?.message || 'If an account exists for that email, a password reset link has been sent.');
    } catch (err) {
      setForgotStatus('error');
      setForgotMessage(err?.response?.data?.detail || 'Could not process your request. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-950 border border-neutral-800 text-white sm:max-w-[440px] p-0 overflow-hidden">
        <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
        <div className="p-7">
          <DialogHeader className="space-y-1.5">
            <p className="text-[12px] uppercase tracking-[0.18em] text-blue-400">MetaxuOne</p>
            <DialogTitle className="text-[22px] leading-tight font-semibold tracking-tight">
              {mode === 'signup' ? 'Create your account' : mode === 'forgot' ? 'Reset your password' : 'Welcome back'}
            </DialogTitle>
            <DialogDescription className="text-[13.5px] text-neutral-400 leading-relaxed">
              {mode === 'signup'
                ? 'Sign up with your company email to access the Partner or Employee Portal.'
                : mode === 'forgot'
                  ? 'Enter your account email and we will send you a link to set a new password.'
                  : 'Sign in to access the Partner or Employee Portal.'}
            </DialogDescription>
          </DialogHeader>

          {mode === 'forgot' ? (
            <div className="mt-5">
              {forgotStatus === 'sent' ? (
                <div className="rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-[13.5px] text-blue-200 leading-relaxed">
                  {forgotMessage}
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-4" noValidate>
                  <div className="space-y-1.5">
                    <Label htmlFor="fp-email" className="text-[13px] text-neutral-300">Email</Label>
                    <Input
                      id="fp-email"
                      type="email"
                      autoComplete="email"
                      value={forgotEmail}
                      onChange={(e) => { setForgotEmail(e.target.value); if (forgotStatus === 'error') setForgotStatus('idle'); }}
                      placeholder="you@yourcompany.com"
                      className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11"
                    />
                  </div>
                  {forgotStatus === 'error' && (
                    <div className="flex items-start gap-2 rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2.5">
                      <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <p className="text-[13px] text-red-300 leading-snug">{forgotMessage}</p>
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={forgotStatus === 'loading'}
                    className="w-full h-11 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {forgotStatus === 'loading' ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending link…</>
                    ) : (
                      <>Send reset link <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                </form>
              )}
              <p className="mt-5 text-center text-[12.5px] text-neutral-500">
                <button type="button" onClick={() => { setMode('signin'); setForgotStatus('idle'); setForgotMessage(''); }} className="text-blue-400 hover:text-blue-300 underline">
                  Back to sign in
                </button>
              </p>
            </div>
          ) : (
          <Tabs value={mode} onValueChange={(v) => { setMode(v); setErrors({}); setServerMessage(''); setStatus('idle'); }} className="mt-5">
            <TabsList className="grid grid-cols-2 bg-neutral-900 border border-neutral-800 p-1 h-10">
              <TabsTrigger value="signin" className="data-[state=active]:bg-neutral-950 data-[state=active]:text-white text-neutral-400 text-[13px] font-medium">
                Sign in
              </TabsTrigger>
              <TabsTrigger value="signup" className="data-[state=active]:bg-neutral-950 data-[state=active]:text-white text-neutral-400 text-[13px] font-medium">
                Create account
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="mt-5">
              <form onSubmit={handleSignin} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="si-email" className="text-[13px] text-neutral-300">Email</Label>
                  <Input
                    id="si-email"
                    type="email"
                    autoComplete="email"
                    value={signin.email}
                    onChange={setField('signin', 'email')}
                    placeholder="you@yourcompany.com"
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11"
                  />
                  {errors.email && <p className="text-[12px] text-red-400">{errors.email}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="si-pwd" className="text-[13px] text-neutral-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="si-pwd"
                      type={showPwd ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={signin.password}
                      onChange={setField('signin', 'password')}
                      placeholder="Your password"
                      className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute inset-y-0 right-0 px-3 text-neutral-500 hover:text-neutral-300"
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-[12px] text-red-400">{errors.password}</p>}
                  <div className="text-right">
                    <button type="button" onClick={() => { setMode('forgot'); setForgotEmail(signin.email); }} className="text-[12.5px] text-blue-400 hover:text-blue-300">
                      Forgot password?
                    </button>
                  </div>
                </div>

                {status === 'error' && (
                  <div className="flex items-start gap-2 rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[13px] text-red-300 leading-snug">{serverMessage}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full h-11 mt-1 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {status === 'loading' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>
                  ) : (
                    <>Sign in <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>

                <p className="text-center text-[12.5px] text-neutral-500">
                  New to MetaxuOne?{' '}
                  <button type="button" onClick={() => { setMode('signup'); setErrors({}); setServerMessage(''); }} className="text-blue-400 hover:text-blue-300 underline">
                    Create an account
                  </button>
                </p>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-5">
              <form onSubmit={handleSignup} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="su-name" className="text-[13px] text-neutral-300">Full name</Label>
                  <Input
                    id="su-name"
                    autoComplete="name"
                    value={signup.name}
                    onChange={setField('signup', 'name')}
                    placeholder="Jane Doe"
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11"
                  />
                  {errors.name && <p className="text-[12px] text-red-400">{errors.name}</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="su-email" className="text-[13px] text-neutral-300">Company email</Label>
                  <Input
                    id="su-email"
                    type="email"
                    autoComplete="email"
                    value={signup.email}
                    onChange={setField('signup', 'email')}
                    placeholder="you@yourcompany.com"
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11"
                  />
                  {errors.email
                    ? <p className="text-[12px] text-red-400">{errors.email}</p>
                    : <p className="text-[11.5px] text-neutral-500">Free email providers are not accepted.</p>}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="su-pwd" className="text-[13px] text-neutral-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="su-pwd"
                      type={showPwd ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={signup.password}
                      onChange={setField('signup', 'password')}
                      placeholder="At least 8 characters"
                      className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute inset-y-0 right-0 px-3 text-neutral-500 hover:text-neutral-300"
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-[12px] text-red-400">{errors.password}</p>}
                </div>

                {status === 'error' && (
                  <div className="flex items-start gap-2 rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2.5">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[13px] text-red-300 leading-snug">{serverMessage}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full h-11 mt-1 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {status === 'loading' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</>
                  ) : (
                    <>Create account <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>

                <p className="text-center text-[12.5px] text-neutral-500">
                  Already have an account?{' '}
                  <button type="button" onClick={() => { setMode('signin'); setErrors({}); setServerMessage(''); }} className="text-blue-400 hover:text-blue-300 underline">
                    Sign in
                  </button>
                </p>
              </form>
            </TabsContent>
          </Tabs>
          )}

          <p className="mt-5 text-center text-[11.5px] text-neutral-500 leading-snug">
            By continuing, you accept our{' '}
            <a href="/terms" className="underline hover:text-neutral-300">Terms</a> and{' '}
            <a href="/privacy" className="underline hover:text-neutral-300">Privacy Policy</a>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignInDialog;
